import React, { Fragment, lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import AuthGuard from 'src/components/AuthGuard';
import CrossAppRedirect from 'src/components/CrossAppRedirect';
import LoadingScreen from 'src/components/LoadingScreen';
import routeSplitMap from 'src/config/routeSplitMap.json';
import { buildExternalUrl, getContentBaseUrl, normalizeTargetApp, isContentTarget } from 'src/config/appTargets';
import DashboardLayout from 'src/layouts/DashboardLayout';
import { pathJoin } from 'src/utilities/Utilities';
import NotFoundView from 'src/views/errors/NotFoundView';
import useAuth from './hooks/useAuth';

const ROUTE_SPLIT_MAP = routeSplitMap?.routes || {};

const buildCompositeKey = (routeKey, parentCompositeKey) => {
  if (!routeKey) {
    return parentCompositeKey || '';
  }

  return parentCompositeKey ? `${parentCompositeKey}.${routeKey}` : routeKey;
};

const resolveSplitMetadata = (route, parent) => {
  const candidates = [];

  if (route?.key) {
    candidates.push(route.key);
  }

  if (parent?.key) {
    candidates.push(buildCompositeKey(route.key, parent.key));
  }

  if (parent?.splitCompositeKey) {
    candidates.push(buildCompositeKey(route.key, parent.splitCompositeKey));
  }

  for (const candidate of candidates) {
    if (candidate && ROUTE_SPLIT_MAP[candidate]) {
      return { key: candidate, metadata: ROUTE_SPLIT_MAP[candidate] };
    }
  }

  return { key: candidates[candidates.length - 1] || route.key, metadata: undefined };
};

const annotateSplitTarget = (route, parent) => {
  const { key: compositeKey, metadata } = resolveSplitMetadata(route, parent);
  const normalizedTarget = normalizeTargetApp(metadata?.targetApp);

  route.splitCompositeKey = compositeKey;
  route.splitTarget = normalizedTarget;
  route.splitTargetRaw = metadata?.targetApp || 'lanternal';

  if (isContentTarget(normalizedTarget)) {
    const baseUrl = getContentBaseUrl();

    if (baseUrl) {
      route.externalUrl = buildExternalUrl(baseUrl, route.path);
      route.component = CrossAppRedirect;
    } else {
      route.externalUrl = null;
    }
  } else {
    route.externalUrl = null;
  }

  return route;
};

const getRoutes = (user) => {
  const flattenRoutes = (routes, parent = {}) => {
    let flatRoutes = [];

    routes.forEach((route) => {
      let finalPath;
      if (route.path === '' && parent.path) {
        finalPath = parent.path;
      } else if (parent.path) {
        finalPath = pathJoin(parent.path, route.path);
      } else {
        finalPath = route.path;
      }

      const processedRoute = {
        ...route,
        path: finalPath,
        guard: route.guard || parent.guard,
        layout: route.layout || parent.layout,
      };

      processedRoute.accessible = isRouteAccessible(processedRoute, parent, user);
      annotateSplitTarget(processedRoute, parent);

      if (processedRoute.routes) {
        if (processedRoute.component && processedRoute.accessible) {
          flatRoutes.push({
            ...processedRoute,
            routes: undefined,
          });
        }
        const childRoutes = flattenRoutes(processedRoute.routes, processedRoute);
        flatRoutes.push(...childRoutes);
      } else if (processedRoute.accessible) {
        flatRoutes.push(processedRoute);
      }
    });

    return flatRoutes;
  };

  const processRoutes = (routes, parent = {}) => {
    return routes.map((route) => {
      route.path = pathJoin(parent.path, route.path);
      route.guard = route.guard || parent.guard;

      route.accessible = isRouteAccessible(route, parent, user);
      annotateSplitTarget(route, parent);

      if (route.routes) {
        route.routes = processRoutes(route.routes, route);
      }

      return route;
    });
  };

  const isRouteAccessible = (route, parent, user) => {
    if (user?.groups?.indexOf('Lantern Admin') != -1) {
      return true;
    }

    return isAccessible(parent, route, user);
  };

  const isAccessible = (parent, route, user) => {
    return parent?.accessible === false ? false : userHasAccessToRoute(route, user);
  };

  const userHasAccessToRoute = (route, user) => {
    return route.groups && route.groups.length ? isUserInAnyGroup(user, route.groups) : true;
  };

  const isUserInAnyGroup = (user, groups) => {
    return groups.some((group) => user?.groups?.indexOf(group) != -1);
  };

  const objectify = (routes) => {
    let obj = {};

    routes.forEach((route) => {
      obj[route.key] = {
        route: {
          accessible: route.accessible,
          path: route.path,
          splitTarget: route.splitTarget,
          splitTargetRaw: route.splitTargetRaw,
          externalUrl: route.externalUrl,
        },
        ...(route.routes ? objectify(route.routes) : {}),
      };
    });

    return obj;
  };

  let routeDefinitions = [
    {
      key: 'login',
      path: '/login',
      component: lazy(() => import('src/views/auth/LoginView/index')),
    },
    {
      key: 'error404',
      path: '/404',
      component: NotFoundView,
    },
    {
      key: 'error403',
      path: '/403',
      component: lazy(() => import('src/views/errors/ForbiddenView')),
    },
    {
      key: 'timedOut',
      path: '/timed-out',
      component: lazy(() => import('src/views/errors/TimedOutView')),
    },
    {
      key: 'clientReview',
      path: '/client-review/client/:parentClient',
      guard: AuthGuard,
      layout: DashboardLayout,
      routes: [
        {
          key: 'clientReviewList',
          path: '',
          index: true,
          component: Fragment,
        },
        {
          key: 'clientReviewEdit',
          path: '/channelProduct/:channelProductId/:placeHolder',
          component: Fragment,
        },
        {
          key: 'clientAssetManager',
          path: '/assetManager/:placeHolder',
          component: Fragment,
        },
      ],
    },
    {
      key: 'app',
      path: '/',
      guard: AuthGuard,
      layout: DashboardLayout,
      routes: [
        {
          key: 'home',
          path: '/',
          component: lazy(() => import('src/views/home/HomeView')),
        },
        {
          key: 'products',
          path: '/products',
          groups: ['Harvest Employees'],
          routes: [
            {
              key: 'list',
              path: '',
              index: true,
              component: Fragment,
              groups: ['Harvest Employees'],
            },
            {
              key: 'create',
              path: '/create',
              component: Fragment,
              groups: ['Harvest Employees'],
            },
          ],
        },
        {
          key: 'content',
          path: '/content',
          groups: ['Harvest Employees'],
          routes: [
            {
              key: 'list',
              path: '',
              index: true,
              component: Fragment,
              groups: ['Harvest Employees'],
            },
            {
              key: 'contentCreation',
              path: '/content-creation',
              groups: ['Harvest Employees'],
              routes: [
                {
                  key: 'keywording',
                  path: '/keywording',
                  component: Fragment,
                  groups: ['Harvest Employees'],
                },
              ],
            },
          ],
        },
        {
          key: 'clientConfiguration',
          path: '/client-configuration',
          component: Fragment,
          groups: ['Harvest Employees'],
        },
        {
          key: 'retailerConfiguration',
          path: '/retailer-configuration',
          component: Fragment,
          groups: ['lantern_Content_Admin'],
        },
        {
          key: 'assetManager',
          path: '/asset-manager',
          component: Fragment,
          groups: ['Harvest Employees', 'Lantern - Content Members'],
        },
        {
          key: 'enhancedContent',
          path: '/enhanced-content',
          groups: ['Lantern Content Specialists'],
          routes: [
            {
              key: 'list',
              path: '',
              index: true,
              component: Fragment,
              groups: ['Lantern Content Specialists'],
            },
            {
              key: 'marketingContent',
              path: '/marketing-content',
              component: Fragment,
              groups: ['Lantern Content Specialists'],
            },
          ],
        },
        {
          key: 'keywordManager',
          path: '/keyword-manager',
          component: Fragment,
          groups: ['Harvest Employees'],
        },
        {
          key: 'quickKeywording',
          path: '/quick-keywording',
          component: Fragment,
          groups: ['Harvest Employees'],
        },
        {
          key: 'emberDatasetManager',
          path: '/ember-dataset-manager',
          component: lazy(() => import('src/views/emberDataManagementTools/emberDatasetManager/index')),
          groups: ['Harvest Employees'],
        },
        {
          key: 'clientGoalsTracker',
          path: '/client-goals-tracker',
          component: lazy(() => import('src/views/emberDataManagementTools/clientGoalsTracker/index')),
          groups: ['Harvest Employees'],
        },
        {
          key: 'permissions',
          path: '/permissions',
          component: lazy(() => import('src/views/permissions/index')),
          groups: ['Harvest Employees'],
        },
        {
          key: 'forecastRoundtableTool',
          path: '/forecast-roundtable-tool',
          component: lazy(() => import('src/views/forecastRoundtable/index')),
          groups: ['Harvest Employees'],
        },
        {
          key: 'forecastRoundtableManager',
          path: '/forecast-roundtable-manager',
          component: lazy(() => import('src/views/forecastRoundtableManager/index')),
          groups: ['Lantern Forecast Roundtable Managers Security'],
        },
        {
          key: 'foremanNetsuiteMappingTool',
          path: '/foreman-netsuite-mapping-tool',
          component: lazy(() => import('src/views/foremanNetsuiteMappingTool/index')),
          groups: ['Lantern Forecast Roundtable Editors Security'],
        },
        {
          key: 'pbcsFileUploader',
          path: '/pbcs-file-uploader',
          component: lazy(() => import('src/views/PBCSFileUploader/index')),
          groups: ['PBCS Export Uploaders'],
        },
        {
          key: 'clientAttributionTool',
          path: '/client-attribution',
          groups: ['Harvest Employees'],
          routes: [
            {
              key: 'list',
              path: '',
              index: true,
              component: lazy(() => import('src/views/clientAttribution/index')),
              groups: ['Harvest Employees'],
            },
            {
              key: 'detailViewWithParentSelected',
              path: '/parentClient/:parentClientId',
              component: lazy(() => import('src/views/clientAttribution/ClientAttributionDetailView')),
              groups: ['Harvest Employees'],
            },
            {
              key: 'detailViewWithParentAndContractSelected',
              path: '/parentClient/:parentClientId/clientContract/:clientContractId',
              component: lazy(() => import('src/views/clientAttribution/ClientAttributionDetailView')),
              groups: ['Harvest Employees'],
            },
          ],
        },
        {
          key: 'clientAttributionManager',
          path: '/client-attribution-manager',
          component: lazy(() => import('src/views/clientAttributionManager/index')),
          groups: ['Lantern Client Attribution Security'],
        },
        {
          key: 'clientManager',
          path: '/client-manager',
          component: lazy(() => import('src/views/clientManager/index')),
          groups: ['Lantern_Feature_ClientCreationForm'],
        },
        {
          key: 'clientOnboarding',
          path: '/client-onboarding',
          component: lazy(() => import('src/views/clientOnboarding/index')),
          groups: ['Harvest Employees'],
        },
      ],
    },
  ];

  let arrayRoutes = flattenRoutes(routeDefinitions);
  let nestedRoutes = processRoutes(JSON.parse(JSON.stringify(routeDefinitions)));
  const objectRoutes = objectify(nestedRoutes);

  return {
    routes: arrayRoutes,
    objectRoutes,
  };
};

const AppRoutes = ({ routes, objectRoutes }) => {
  const { user } = useAuth();

  if (!routes || !objectRoutes) {
    const rootRoutes = getRoutes(user);
    routes = rootRoutes.routes;
    objectRoutes = rootRoutes.objectRoutes;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {routes?.map((route, i) => {
          const Guard = route.guard || Fragment;
          const Layout = route.layout || Fragment;
          const Component = route.component;
          const routeProps = { path: route.path };

          return (
            <Route
              key={i}
              {...routeProps}
              element={
                <Guard route={route}>
                  <Layout {...(Layout !== Fragment && { routes: objectRoutes })}>
                    <Component route={route} />
                  </Layout>
                </Guard>
              }
            />
          );
        })}
        <Route path="*" element={<NotFoundView />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
